import { AnimatePresence, motion } from 'framer-motion';
import {
  Ban,
  Box,
  MoreHorizontal,
  Package,
  ShieldAlert,
  Trash2,
  User,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { FadeIn } from '../components/AnimatedPanel';
import { useAuth } from '../context/AuthContext';
import type { FriendshipStatus, PublicUser } from '../types';

interface ProfilePanelProps {
  username?: string;
  onNavigate: (panel: string) => void;
}

export function ProfilePanel({ username, onNavigate }: ProfilePanelProps) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const targetUsername = username ?? currentUser?.username;
  const [profile, setProfile] = useState<PublicUser | null>(null);
  const [friendshipStatus, setFriendshipStatus] = useState<FriendshipStatus>('none');
  const [canAdmin, setCanAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [banDays, setBanDays] = useState(7);
  const [banReason, setBanReason] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const isOwnProfile = currentUser?.username.toLowerCase() === targetUsername?.toLowerCase();

  const loadProfile = useCallback(async () => {
    if (!targetUsername) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getUser(targetUsername);
      setProfile(data.user);
      setFriendshipStatus(data.friendshipStatus);
      setCanAdmin(data.canAdmin);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [targetUsername]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || menuButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  useLayoutEffect(() => {
    if (!menuOpen || !menuButtonRef.current) {
      setMenuPos(null);
      return;
    }
    function updatePosition() {
      const btn = menuButtonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const menuWidth = 224;
      const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
      setMenuPos({ top: rect.bottom + 8, left });
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [menuOpen]);

  async function sendFriendRequest() {
    if (!profile) return;
    try {
      await api.sendFriendRequest(profile.username);
      setFriendshipStatus('pending_out');
      setActionMsg('Friend request sent.');
      setMenuOpen(false);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'Failed.');
    }
  }

  async function removeFriend() {
    if (!profile) return;
    await api.removeFriend(profile.username);
    setFriendshipStatus('none');
    setActionMsg('Friend removed.');
    setMenuOpen(false);
  }

  async function deleteUser() {
    if (!profile || !confirm(`Permanently delete ${profile.username}'s account?`)) return;
    try {
      await api.adminDeleteUser(profile.username);
      setActionMsg(`${profile.username} deleted.`);
      navigate('/');
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'Failed.');
    }
  }

  async function banUser() {
    if (!profile) return;
    try {
      await api.adminBanUser(profile.username, banDays, banReason);
      setBanOpen(false);
      setActionMsg(`${profile.username} banned for ${banDays} days.`);
      await loadProfile();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'Failed.');
    }
  }

  async function unbanUser() {
    if (!profile) return;
    await api.adminUnbanUser(profile.username);
    setActionMsg(`${profile.username} unbanned.`);
    await loadProfile();
  }

  if (!targetUsername) {
    return (
      <div className="panel-card text-center">
        <p className="text-korvex-muted">Sign in to view your profile.</p>
      </div>
    );
  }

  if (loading) return <p className="text-korvex-muted">Loading profile…</p>;
  if (error || !profile) return <p className="text-red-400">{error || 'Profile not found.'}</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <FadeIn>
        <div className="panel-card relative">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 overflow-hidden rounded-t-2xl">
            <div
              className="h-full w-full opacity-40"
              style={{
                backgroundImage: `linear-gradient(135deg, ${profile.avatarColor}, #22d3ee)`,
              }}
            />
          </div>
          <div className="relative flex flex-wrap items-start justify-between gap-4 pt-16">
            <div className="flex items-end gap-4">
              <span
                className="flex h-24 w-24 items-center justify-center rounded-2xl border-4 border-korvex-bg text-3xl font-bold text-white shadow-glow"
                style={{ backgroundColor: profile.avatarColor }}
              >
                {profile.username[0]?.toUpperCase()}
              </span>
              <div>
                <h2 className="font-display text-3xl font-bold">{profile.username}</h2>
                <p className="text-sm text-korvex-muted">
                  Joined {new Date(profile.createdAt).toLocaleDateString()}
                </p>
                {profile.isBanned && (
                  <p className="mt-1 text-sm text-red-400">
                    Banned until {profile.bannedUntil ? new Date(profile.bannedUntil).toLocaleString() : '—'}
                    {profile.banReason ? ` — ${profile.banReason}` : ''}
                  </p>
                )}
              </div>
            </div>

            <div className="relative z-10">
              <button
                ref={menuButtonRef}
                type="button"
                className="btn-ghost rounded-full p-2.5"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="More options"
                aria-expanded={menuOpen}
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>
          </div>

          <p className="relative mt-6 text-korvex-muted">{profile.bio || 'No bio yet.'}</p>

          {!isOwnProfile && friendshipStatus === 'friends' && (
            <span className="mt-4 inline-flex items-center gap-1 rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-400">
              <UserCheck className="h-3.5 w-3.5" />
              Friends
            </span>
          )}
        </div>
      </FadeIn>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {menuOpen && menuPos && (
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ duration: 0.2 }}
                style={{ top: menuPos.top, left: menuPos.left }}
                className="fixed z-[200] w-56 overflow-hidden rounded-xl border border-white/10 bg-korvex-card shadow-glow-violet"
              >
                <MenuItem icon={User} label="Profile" onClick={() => { setMenuOpen(false); }} />
                {isOwnProfile ? (
                  <>
                    <MenuItem icon={Package} label="Inventory" onClick={() => { setMenuOpen(false); onNavigate('inventory'); }} />
                    <MenuItem icon={Box} label="My models" subtitle="Coming soon" disabled />
                  </>
                ) : (
                  <>
                    <MenuItem icon={Package} label="View inventory" subtitle="Coming soon" disabled />
                    {friendshipStatus === 'none' && (
                      <MenuItem icon={UserPlus} label="Send friend request" onClick={() => void sendFriendRequest()} />
                    )}
                    {friendshipStatus === 'pending_out' && (
                      <MenuItem icon={UserPlus} label="Request pending" disabled />
                    )}
                    {friendshipStatus === 'pending_in' && (
                      <MenuItem icon={UserCheck} label="Respond in Friends" onClick={() => { setMenuOpen(false); onNavigate('friends'); }} />
                    )}
                    {friendshipStatus === 'friends' && (
                      <MenuItem icon={UserCheck} label="Remove friend" onClick={() => void removeFriend()} />
                    )}
                  </>
                )}
                {canAdmin && (
                  <>
                    <div className="my-1 border-t border-white/10" />
                    <MenuItem icon={Ban} label="Ban account" danger onClick={() => { setMenuOpen(false); setBanOpen(true); }} />
                    {profile.isBanned && (
                      <MenuItem icon={ShieldAlert} label="Unban account" onClick={() => void unbanUser()} />
                    )}
                    <MenuItem icon={Trash2} label="Delete account" danger onClick={() => void deleteUser()} />
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}

      {actionMsg && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-korvex-accent">
          {actionMsg}
        </motion.p>
      )}

      <AnimatePresence>
        {banOpen && (
          <motion.div className="fixed inset-0 z-[300] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button type="button" className="absolute inset-0 bg-black/60" onClick={() => setBanOpen(false)} aria-label="Close" />
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95 }}
              className="panel-card relative z-10 w-full max-w-md space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-bold">Ban {profile.username}</h3>
                <button type="button" onClick={() => setBanOpen(false)}><X className="h-5 w-5" /></button>
              </div>
              <div>
                <label className="mb-1 block text-sm">Duration (days, min 3)</label>
                <input type="number" min={3} className="input-field" value={banDays} onChange={(e) => setBanDays(Number(e.target.value))} />
              </div>
              <div>
                <label className="mb-1 block text-sm">Reason</label>
                <textarea className="input-field min-h-[80px]" value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder="Reason for ban…" />
              </div>
              <button type="button" className="btn-primary w-full bg-red-600 from-red-600 to-red-700" onClick={() => void banUser()}>
                Confirm ban
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  subtitle,
  onClick,
  disabled,
  danger,
}: {
  icon: typeof User;
  label: string;
  subtitle?: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40 ${
        danger ? 'text-red-400' : ''
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>
        {label}
        {subtitle && <span className="block text-xs text-korvex-muted">{subtitle}</span>}
      </span>
    </button>
  );
}
